import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Trash2, Plus, Upload, RefreshCw } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandling';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import QuestionForm from './QuestionForm';
import BulkUploadModal from './BulkUploadModal';
import ViewQuestionModal from '../Questions/ViewQuestionModal';

const QuestionList = ({
  isDraft = false, // Whether this is for draft questions
  title = 'Questions',
  apiEndpoint = '/api/super-admin/questions', // Base API endpoint
  showActions = true, // Whether to show action buttons
  showFilters = true, // Whether to show filter controls
  onQuestionSelect = null, // Callback for question selection
  allowCreate = true, // Whether to allow creating new questions
  allowBulkUpload = true, // Whether to allow bulk upload
  allowEdit = true, // Whether to allow editing questions
  allowDelete = true, // Whether to allow deleting questions
  customActions = [], // Custom action buttons
  workflowActions = [] // Workflow-specific actions (for draft questions)
}) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    topic: '',
    difficulty: '',
    examType: '',
    questionType: '',
    tenantId: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    subjects: [],
    topics: [],
    difficulties: [],
    examTypes: [],
    questionTypes: [],
    tenants: []
  });

  useEffect(() => {
    loadQuestions();
    loadFilterOptions();
  }, [pagination.page, pagination.size, searchTerm, filters]);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        search: searchTerm,
        ...filters
      };

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await superAdminAPI.getQuestions(params, apiEndpoint);
      
      if (response.data) {
        setQuestions(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      setError('Failed to load questions. Please try again.');
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [
        subjectsResponse,
        difficultiesResponse,
        examTypesResponse,
        questionTypesResponse,
        tenantsResponse
      ] = await Promise.all([
        superAdminAPI.getSubjects(),
        superAdminAPI.getDifficultyLevels(),
        superAdminAPI.getExamTypes(),
        superAdminAPI.getQuestionTypes(),
        isDraft ? Promise.resolve({ data: [] }) : superAdminAPI.getAllTenants({ size: 100 })
      ]);

      setFilterOptions({
        subjects: subjectsResponse.data || [],
        difficulties: difficultiesResponse.data || [],
        examTypes: examTypesResponse.data || [],
        questionTypes: questionTypesResponse.data || [],
        tenants: tenantsResponse.data?.content || []
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleCreateQuestion = async (questionData) => {
    try {
      const endpoint = isDraft ? '/api/super-admin/draft-questions' : '/api/super-admin/questions';
      await superAdminAPI.createQuestion(questionData, endpoint);
      showSuccessToast(`${isDraft ? 'Draft question' : 'Question'} created successfully`);
      loadQuestions();
    } catch (error) {
      console.error('Error creating question:', error);
      showErrorToast(error);
      throw error; // Re-throw to prevent modal from closing
    }
  };

  const handleEditQuestion = async (questionData) => {
    try {
      const endpoint = isDraft 
        ? `/api/super-admin/draft-questions/${selectedQuestion.id}`
        : `/api/super-admin/questions/${selectedQuestion.id}`;
      await superAdminAPI.updateQuestion(selectedQuestion.id, questionData, endpoint);
      showSuccessToast(`${isDraft ? 'Draft question' : 'Question'} updated successfully`);
      loadQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      showErrorToast(error);
      throw error; // Re-throw to prevent modal from closing
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm(`Are you sure you want to delete this ${isDraft ? 'draft question' : 'question'}?`)) {
      return;
    }

    try {
      const endpoint = isDraft 
        ? `/api/super-admin/draft-questions/${questionId}`
        : `/api/super-admin/questions/${questionId}`;
      await superAdminAPI.deleteQuestion(questionId, endpoint);
      showSuccessToast(`${isDraft ? 'Draft question' : 'Question'} deleted successfully`);
      loadQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      showErrorToast(error);
    }
  };

  const handleBulkUploadComplete = (result) => {
    showSuccessToast(`Bulk upload completed: ${result.successCount} questions uploaded`);
    loadQuestions();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const openQuestionForView = async (question) => {
    setSelectedQuestion(question);
    setShowViewModal(true);

    if (!question?.id) return;

    try {
      const response = isDraft
        ? await superAdminAPI.getDraftQuestionById(question.id)
        : await superAdminAPI.getQuestionById(question.id);

      if (response?.data) {
        setSelectedQuestion(response.data);
      }
    } catch (error) {
      console.error('Error loading question details for view modal:', error);
    }
  };

  const handleQuestionClick = (question) => {
    if (onQuestionSelect) {
      onQuestionSelect(question);
    } else {
      openQuestionForView(question);
    }
  };

  const renderQuestionCard = (question) => (
    <div
      key={question.id}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => handleQuestionClick(question)}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
            {question.text || question.questionText}
          </h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {question.subject && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {question.subject}
              </span>
            )}
            {question.topic && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {question.topic}
              </span>
            )}
            {question.difficulty && (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                question.difficulty === 'EASY' ? 'bg-green-100 text-green-800' :
                question.difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {question.difficulty}
              </span>
            )}
            {question.questionType && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {question.questionType.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>
        
        {showActions && (
          <div className="flex space-x-1 ml-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openQuestionForView(question);
              }}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              title="View"
            >
              <Eye className="w-4 h-4" />
            </button>
            {allowEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedQuestion(question);
                  setShowEditModal(true);
                }}
                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {allowDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteQuestion(question.id);
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {/* Workflow actions for draft questions */}
            {workflowActions.map((action, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(question);
                }}
                className={`p-1 transition-colors ${action.className || 'text-gray-400 hover:text-blue-600'}`}
                title={action.title}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Question preview */}
      <div className="text-xs text-gray-500 mt-2">
        {question.examTypes && question.examTypes.length > 0 && (
          <div>Exam Types: {question.examTypes.join(', ')}</div>
        )}
        {question.tags && question.tags.length > 0 && (
          <div>Tags: {question.tags.join(', ')}</div>
        )}
        {isDraft && question.status && (
          <div className="mt-1">
            Status: <span className={`font-medium ${
              question.status === 'DRAFT' ? 'text-yellow-600' :
              question.status === 'UNDER_REVIEW' ? 'text-blue-600' :
              question.status === 'APPROVED' ? 'text-green-600' :
              'text-red-600'
            }`}>
              {question.status.replace(/_/g, ' ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <div className="flex space-x-3">
          {customActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={action.className || 'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'}
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </button>
          ))}
          {allowBulkUpload && (
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </button>
          )}
          {allowCreate && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {isDraft ? 'Draft ' : ''}Question
            </button>
          )}
          <button
            onClick={loadQuestions}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search questions..."
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <select
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subjects</option>
                {filterOptions.subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                value={filters.difficulty}
                onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Difficulties</option>
                {filterOptions.difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exam Type</label>
              <select
                value={filters.examType}
                onChange={(e) => handleFilterChange('examType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Exam Types</option>
                {filterOptions.examTypes.map(examType => (
                  <option key={examType} value={examType}>{examType}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
              <select
                value={filters.questionType}
                onChange={(e) => handleFilterChange('questionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Question Types</option>
                {filterOptions.questionTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            
            {!isDraft && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
                <select
                  value={filters.tenantId}
                  onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Tenants</option>
                  <option value="null">Cross-Tenant</option>
                  {filterOptions.tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.code})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <CardLoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} onRetry={loadQuestions} />
      ) : (
        <>
          {/* Results Summary */}
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>
              Showing {questions.length} of {pagination.totalElements} {isDraft ? 'draft questions' : 'questions'}
            </span>
            <span>
              Page {pagination.page + 1} of {pagination.totalPages}
            </span>
          </div>

          {/* Questions Grid */}
          {questions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg mb-2">No {isDraft ? 'draft questions' : 'questions'} found</div>
              <div className="text-gray-400 text-sm">
                {allowCreate && (
                  <>Try adjusting your filters or <button
                    onClick={() => setShowCreateModal(true)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    create a new {isDraft ? 'draft ' : ''}question
                  </button></>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {questions.map(renderQuestionCard)}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = Math.max(0, Math.min(pagination.totalPages - 5, pagination.page - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 border rounded-md ${
                      pageNum === pagination.page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showCreateModal && (
        <QuestionForm
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateQuestion}
          title={`Add ${isDraft ? 'Draft ' : ''}Question`}
          submitButtonText={`Create ${isDraft ? 'Draft ' : ''}Question`}
          isDraft={isDraft}
        />
      )}

      {showEditModal && selectedQuestion && (
        <QuestionForm
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedQuestion(null);
          }}
          onSubmit={handleEditQuestion}
          initialData={selectedQuestion}
          mode="edit"
          title={`Edit ${isDraft ? 'Draft ' : ''}Question`}
          submitButtonText={`Update ${isDraft ? 'Draft ' : ''}Question`}
          isDraft={isDraft}
        />
      )}

      {showViewModal && selectedQuestion && (
        <ViewQuestionModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedQuestion(null);
          }}
          question={selectedQuestion}
        />
      )}

      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSubmit={handleBulkUploadComplete}
          isDraft={isDraft}
          title={`Bulk Upload ${isDraft ? 'Draft ' : ''}Questions`}
        />
      )}
    </div>
  );
};

export default QuestionList;
